import { CopyOutlined, FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Badge,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Form,
  Modal,
  Radio,
  Switch,
  Tag,
  Tooltip,
} from 'antd';
import { filter, forEach, isEmpty, map } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as ArchiveIcon } from '../../../../../assets/svg/box-archive-solid.svg';
import { ReactComponent as ActiveIcon } from '../../../../../assets/svg/layer-group-solid.svg';
import { ReactComponent as ResetpasswordIcon } from '../../../../../assets/svg/lock-solid.svg';
import { ReactComponent as UpdateIcon } from '../../../../../assets/svg/pen-solid.svg';
import { ReactComponent as EditIcon } from '../../../../../assets/svg/pen-to-square-solid.svg';
import { ReactComponent as UnarchiveIcon } from '../../../../../assets/svg/right-from-bracket-solid.svg';
import { ReactComponent as WarehouseIcon } from '../../../../../assets/svg/warehouse.svg';
import {
  ARCHIVING_STATE,
  BREAKPOINTS,
  DATE_FORMATS,
  LIBRARY_FACILITY_TYPE,
  LIMIT,
  PERMISSIONS_KEY,
  PERMISSION_TYPE,
  ROLE_KEYS,
  ROUTES,
  SORT,
  STATUS,
  TIME_FORMATS,
  USER_FORM_STATUS,
} from '../../../../../common/constants';
import {
  formatDate,
  handleCopy,
  hasPermission,
} from '../../../../../common/utils';
import CommonTable from '../../../../../components/CommonTable';
import LoaderComponent from '../../../../../components/LoaderComponent';
import SearchComponent from '../../../../../components/SearchComponent';
import { FORGOT_PASSWORD } from '../../../../auth/graphql/Mutations';
import {
  ASSIGN_BACKUP_LIBRARY,
  ASSIGN_WAREHOUSE,
  UPDATE_LIBRARY_ADMIN,
} from '../graphql/Mutations';
import { LIBRARIES } from '../graphql/Queries';
import AssignDeliveryPartnerModal from './AssignDeliveryPartnerModal';
import BackupLibModal from './BackupLibModal';
import WarehouseModal from './WarehouseModal';

const { RangePicker } = DatePicker;
const { Group } = Radio;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialLibraryFilter = {
  limit: LIMIT,
  search: '',
  skip: 0,
};

const initialLibrarySort = {
  field: 'createdAt',
  order: 'DESC',
};

const LibraryTable = ({ exportFilter, setExportFilter }) => {
  const {
    state: { permissions, currentUser },
  } = useContext(AppContext);
  const { roles } = currentUser;
  const [form] = Form.useForm();
  const [warehouseForm] = Form.useForm();
  const history = useHistory();
  const [libraryList, setLibraryList] = useState([]);
  const [librarySearchTerm, setLibrarySearchTerm] = useState('');
  const [isEmptyLibraryList, setIsEmptyLibraryList] = useState(false);
  const [library, setLibrary] = useState({});
  const [query, setQuery] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);

  const [isActivePrompts, setIsActivePrompts] = useState(false);
  const [isArchivePrompts, setIsArchivePrompts] = useState(false);
  const [isResetPrompts, setIsResetPrompts] = useState(false);

  const [isLibraryLoading, setIsLibraryLoading] = useState(true);
  const [isActiveLoading, setIsActiveLoading] = useState(false);
  const [isArchiveLoading, setIsArchiveLoading] = useState(false);
  const [isResetPassLoading, setIsResetPassLoading] = useState(false);
  const [isAssignLibLoading, setIsAssignLibLoading] = useState(false);
  const [
    isAssignDeliveryPartnerLoading,
    setIsAssignDeliveryPartnerLoading,
  ] = useState(false);
  const [isAssignWarehouseLoading, setIsAssignWarehouseLoading] = useState(
    false,
  );
  const [isArchiveFilter, setIsArchiveFilter] = useState(false);
  const [isBackupLibModalOpen, setBackupLibModalOpen] = useState(false);
  const [filterList, setFilterList] = useState([]);
  const [whereFilter, setWhereFilter] = useState();
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterTags, setFilterTags] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterIndex, setFilterIndex] = useState(null);
  const [finalWhereFilters, setFinalWhereFilters] = useState();
  const [finalFilterTags, setFinalFilterTags] = useState(null);
  const [isUpdateBackup, setIsUpdateBackup] = useState(false);
  const [isDeliveryPartnerModalOpen, setDeliveryPartnerModalOpen] = useState(
    false,
  );
  const [isUpdateDeliveryPartner, setIsUpdateDeliveryPartner] = useState(false);
  const [isWarehouseModalOpen, setIsWarehouseModalOpen] = useState(false);
  const [isUpdateWarehouse, setIsUpdateWarehouse] = useState(false);

  const [forgotPassword] = useMutation(FORGOT_PASSWORD, {
    onError: () => {},
  });
  const [updateLibraryAdmin] = useMutation(UPDATE_LIBRARY_ADMIN, {
    onError: () => {},
  });
  const [assignBackupLibrary] = useMutation(ASSIGN_BACKUP_LIBRARY, {
    onError: () => {},
  });
  const [assignWarehouse] = useMutation(ASSIGN_WAREHOUSE, {
    onError: () => {},
  });

  const [libraries] = useLazyQuery(LIBRARIES, {
    onCompleted: (response) => {
      setLibraryList([...response?.libraries?.data]);
      if (
        response?.libraries?.count === 0 &&
        initialPaginationValue?.total === 0
      ) {
        setIsEmptyLibraryList(true);
      } else {
        setIsEmptyLibraryList(false);
      }
      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.libraries?.count,
      };
      setPaginationProp(pagination);
      setIsLibraryLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const hasUpdatePermission = hasPermission(
    PERMISSIONS_KEY?.MASTER_LIST_LIBRARY,
    PERMISSION_TYPE?.UPDATE,
    permissions,
  );

  useEffect(() => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsLibraryLoading(true);
    libraries({
      variables: {
        filter: {
          ...initialLibraryFilter,
          search: librarySearchTerm,
          isArchived: isArchiveFilter,
          limit: paginationProp?.pageSize || LIMIT,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          isActive:
            finalWhereFilters?.status === true ||
            finalWhereFilters?.status === false
              ? finalWhereFilters?.status
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialLibrarySort,
      },
    });
    setExportFilter({ ...exportFilter, isArchived: isArchiveFilter });
  }, [isArchiveFilter]);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    setIsLibraryLoading(true);
    setSortedInfo(sorter);
    libraries({
      variables: {
        filter: {
          ...initialLibraryFilter,
          skip,
          limit: pagination?.pageSize,
          search: librarySearchTerm,
          isArchived: isArchiveFilter,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          isActive:
            finalWhereFilters?.status === true ||
            finalWhereFilters?.status === false
              ? finalWhereFilters?.status
              : undefined,
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialLibrarySort,
      },
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setLibrarySearchTerm(trimValue);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsLibraryLoading(true);
    libraries({
      variables: {
        filter: {
          ...initialLibraryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: trimValue,
          isArchived: isArchiveFilter,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          isActive:
            finalWhereFilters?.status === true ||
            finalWhereFilters?.status === false
              ? finalWhereFilters?.status
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialLibrarySort,
      },
    });
    setExportFilter({ ...exportFilter, search: trimValue });
  };

  const handleActive = async (id, isActive) => {
    setIsActiveLoading(true);
    const response = await updateLibraryAdmin({
      variables: {
        where: {
          id,
        },
        data: {
          isActive: !isActive,
        },
      },
    });
    if (response?.data) {
      setIsActiveLoading(false);
      setIsLibraryLoading(true);
      setIsActivePrompts(false);
      libraries({
        variables: {
          filter: {
            ...initialLibraryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: librarySearchTerm,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            isActive:
              finalWhereFilters?.status === true ||
              finalWhereFilters?.status === false
                ? finalWhereFilters?.status
                : undefined,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialLibrarySort,
        },
      });
    }
    setIsActiveLoading(false);
  };

  const handleArchive = async (id, isArchived) => {
    setIsArchiveLoading(true);
    const response = await updateLibraryAdmin({
      variables: {
        where: {
          id,
        },
        data: {
          isArchived: !isArchived,
        },
      },
    });
    if (response?.data) {
      setIsArchiveLoading(false);
      setIsLibraryLoading(true);
      setIsArchivePrompts(false);
      libraries({
        variables: {
          filter: {
            ...initialLibraryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: librarySearchTerm,
            isArchived: isArchiveFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            isActive:
              finalWhereFilters?.status === true ||
              finalWhereFilters?.status === false
                ? finalWhereFilters?.status
                : undefined,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialLibrarySort,
        },
      });
    }
    setIsArchiveLoading(false);
  };

  const handleResetPassword = async (email) => {
    setIsResetPassLoading(true);
    const response = await forgotPassword({
      variables: {
        data: {
          email,
        },
      },
    });
    if (response?.data) {
      setIsResetPrompts(false);
    }
    setIsResetPassLoading(false);
  };

  const handleAssignBackupLib = async (values) => {
    setIsAssignLibLoading(true);
    const response = await assignBackupLibrary({
      variables: {
        where: {
          id: library?.id,
        },
        data: {
          backUpLibraryId: values?.name,
        },
      },
    });
    if (response?.data) {
      setBackupLibModalOpen(false);
      setLibrary({});
      form.resetFields();
      setIsAssignLibLoading(false);
      setIsLibraryLoading(true);
      libraries({
        variables: {
          filter: {
            ...initialLibraryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: librarySearchTerm,
            isArchived: isArchiveFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            isActive:
              finalWhereFilters?.status === true ||
              finalWhereFilters?.status === false
                ? finalWhereFilters?.status
                : undefined,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialLibrarySort,
        },
      });
    }
    setIsAssignLibLoading(false);
  };

  const handleAssignWarehouse = async (values) => {
    setIsAssignWarehouseLoading(true);
    const response = await assignWarehouse({
      variables: {
        where: {
          id: library?.id,
        },
        data: {
          warehouseId: values?.name,
        },
      },
    });
    if (response?.data) {
      setIsWarehouseModalOpen(false);
      setLibrary({});
      warehouseForm.resetFields();
      setIsAssignWarehouseLoading(false);
      setIsLibraryLoading(true);
      libraries({
        variables: {
          filter: {
            ...initialLibraryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: librarySearchTerm,
            isArchived: isArchiveFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            isActive:
              finalWhereFilters?.status === true ||
              finalWhereFilters?.status === false
                ? finalWhereFilters?.status
                : undefined,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialLibrarySort,
        },
      });
    }
    setIsAssignWarehouseLoading(false);
  };

  const handleAssignDeliveryPartner = async (values) => {
    setIsAssignDeliveryPartnerLoading(true);
    const response = await updateLibraryAdmin({
      variables: {
        where: {
          id: library?.id,
        },
        data: {
          deliveryPartnerId: values?.name,
        },
      },
    });
    if (response?.data) {
      setDeliveryPartnerModalOpen(false);
      setLibrary({});
      form.resetFields();
      setIsAssignDeliveryPartnerLoading(false);
      setIsLibraryLoading(true);
      libraries({
        variables: {
          filter: {
            ...initialLibraryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: librarySearchTerm,
            isArchived: isArchiveFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            isActive:
              finalWhereFilters?.status === true ||
              finalWhereFilters?.status === false
                ? finalWhereFilters?.status
                : undefined,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialLibrarySort,
        },
      });
    }
    setIsAssignDeliveryPartnerLoading(false);
  };

  const getFilterData = (confirm) => {
    setIsLibraryLoading(true);
    libraries({
      variables: {
        filter: {
          ...initialLibraryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: librarySearchTerm,
          isArchived: isArchiveFilter,
          dateRange: {
            from: whereFilter?.createdAt?.from?.$d,
            to: whereFilter?.createdAt?.to?.$d,
          },
          isActive:
            whereFilter?.status === true || whereFilter?.status === false
              ? whereFilter?.status
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialLibrarySort,
      },
    });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFilter });
    setExportFilter({ ...exportFilter, ...finalWhereFilters, ...whereFilter });
    setFinalFilterTags(filterTags);
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    if (confirm) {
      confirm();
    }
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const tagsCopy = {
      ...filterTags,
      [dataIndex]: [],
    };
    const whereFiltersCopy = {
      ...whereFilter,
      [dataIndex]: [],
    };
    setFilterVisible(false);
    setFinalFilterTags({ ...filterTags, ...tagsCopy });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFiltersCopy });
    setExportFilter({
      ...exportFilter,
      ...finalWhereFilters,
      ...whereFiltersCopy,
    });
    setIsLibraryLoading(true);
    libraries({
      variables: {
        filter: {
          ...initialLibraryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: librarySearchTerm,
          isArchived: isArchiveFilter,
          dateRange: {
            from: whereFiltersCopy?.createdAt?.from?.$d,
            to: whereFiltersCopy?.createdAt?.to?.$d,
          },
          isActive:
            whereFiltersCopy?.status === true ||
            whereFiltersCopy?.status === false
              ? whereFiltersCopy?.status
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialLibrarySort,
      },
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    clearFilters();
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterList([]);
      setFilterLoading(true);
      setFilterTags({ ...filterTags, ...finalFilterTags });
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
      const optionsCopy = [];

      switch (filterIndex) {
        case 'status':
          forEach(STATUS, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.key });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        default:
          break;
      }
    }
    if (!filterVisible) {
      setFilterTags(finalFilterTags);
      setWhereFilter(finalWhereFilters);
    }
  }, [filterVisible]);

  const changeFilter = (newFilterObj, dataIndex) => {
    const {
      target: { value },
    } = newFilterObj;

    const filterTagsCopy = { ...filterTags };
    let newTagList;
    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filter(filterTagsCopy?.[dataIndex], (item) => {
          if (item?.label) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    } else {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filterTagsCopy?.[dataIndex]
          ? [...filterTagsCopy?.[dataIndex], value]
          : [value],
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const handleDeleteFilter = (value, dataIndex) => {
    let newTagList;

    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTags,
        [dataIndex]: filter(filterTags?.[dataIndex], (item) => {
          if (item?.key) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const onRangePickerChange = (values, dataIndex) => {
    const rangeObj = {
      from: values[0]?.startOf('day'),
      to: values?.[1]?.endOf('day'),
    };

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: rangeObj,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const onRadioChange = (value, dataIndex) => {
    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: value,
    };
    if (!value?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const filterPopup = (dataIndex) => ({
    filterDropdown: ({ confirm, clearFilters }) => {
      if (dataIndex === 'createdAt') {
        return (
          <div className="custom-filter-dropdown">
            <RangePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onRangePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.from
                  ? [
                      whereFilter?.[dataIndex]?.from,
                      whereFilter?.[dataIndex]?.to,
                    ]
                  : null
              }
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      if (dataIndex === 'status') {
        return (
          <div className="custom-filter-dropdown">
            <Group
              options={USER_FORM_STATUS}
              value={whereFilter?.[dataIndex]}
              onChange={(e) => onRadioChange(e?.target?.value, dataIndex)}
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="custom-filter-dropdown">
          <LoaderComponent spinning={filterLoading} setHeight={35}>
            {filterTags?.[dataIndex]?.length > 0 && (
              <div className="filter-tags-wrapper">
                {map(filterTags?.[dataIndex], (item) => (
                  <Tag
                    key={item?.key}
                    closable
                    onClose={() => handleDeleteFilter(item, dataIndex)}
                    className="filter-tag"
                  >
                    <span>{item?.label}</span>
                  </Tag>
                ))}
              </div>
            )}
            <div className="filter-checkboxes">
              {filterList?.length > 0 ? (
                map(filterList, (item) => (
                  <div className="single-checkbox-div" key={item?.key}>
                    <Checkbox
                      value={item}
                      checked={whereFilter?.[dataIndex]?.includes(item?.key)}
                      key={item?.key || item}
                      onChange={(e) => changeFilter(e, dataIndex)}
                      className="single-checkbox"
                    >
                      <span>{item?.label}</span>
                    </Checkbox>
                  </div>
                ))
              ) : (
                <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          </LoaderComponent>
          <Divider className="divider-filter" />
          <div className="d-flex justify-center">
            <Button
              size="small"
              className="reset-button"
              id="roles-filter-reset"
              onClick={() => handleReset(clearFilters, dataIndex, confirm)}
            >
              Reset
            </Button>
            <Button
              size="small"
              className="common-button ok-button"
              id="roles-filter-ok"
              type="primary"
              onClick={() => getFilterData(confirm, dataIndex)}
            >
              Ok
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: () =>
      finalWhereFilters?.[dataIndex]?.length > 0 ||
      !isEmpty(finalWhereFilters?.[dataIndex]) ||
      finalWhereFilters?.[dataIndex] === true ||
      finalWhereFilters?.[dataIndex] === false ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (visible) => {
      setFilterIndex(dataIndex);
      setFilterVisible(visible);
    },
  });

  const columns = [
    {
      title: 'LIBRARY NAME',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: true,
      width: 250,
      align: 'left',
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'left' : false,
      className: 'max-width-column',
      sortOrder: sortedInfo?.columnKey === 'name' && sortedInfo?.order,
      render: (_, record) => (
        <>
          <div className="table-data-with-id">{record?.name}</div>
          {record?.libraryNumber ? (
            <div className="font-size-12">({record?.libraryNumber})</div>
          ) : (
            '-'
          )}
        </>
      ),
    },
    {
      title: 'PREFERRED TIME',
      dataIndex: 'preferredTime',
      key: 'preferredTime',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        `${formatDate(
          record?.preferredTimeFrom,
          TIME_FORMATS?.REGULAR,
        )} to ${formatDate(record?.preferredTimeTo, TIME_FORMATS?.REGULAR)}`,
    },
    {
      title: 'CONTACT',
      dataIndex: 'contactNo',
      key: 'contactNo',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.librarian?.contactNo || '-',
    },
    {
      title: 'ONBOARDED ON',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      ellipsis: true,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.REGULAR),
      ...filterPopup('createdAt'),
    },
    {
      title: 'REFERRAL CODE',
      dataIndex: 'referralCode',
      key: 'referralCode',
      ellipsis: true,
      width: 200,
      className: 'max-width-column',
      onCell: () => ({
        onClick: (e) => {
          e?.stopPropagation();
        },
      }),
      render: (_, record) => (
        <div className="d-flex">
          <span className="pr-8">{record?.referralCode}</span>
          <CopyOutlined
            className="copy-icon"
            onClick={() => handleCopy(record?.referralCode)}
          />
        </div>
      ),
    },
    {
      title: 'AVAILABLE BALANCE',
      dataIndex: 'availableBalance',
      key: 'availableBalance',
      ellipsis: true,
      width: 200,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.availableBalance || 0,
    },
    {
      title: 'WAREHOUSE',
      dataIndex: 'warehouse',
      key: 'warehouse',
      ellipsis: true,
      width: 200,
      align: 'left',
      className: 'max-width-column',
      onCell: (record) => ({
        onClick: (e) => {
          e?.stopPropagation();
          if (record?.warehouse?.id) {
            history?.push(
              `${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}${ROUTES?.BASIC_DETAILS}/view/${record?.warehouse?.id}/${record?.warehouse?.libraryNumber}`,
            );
          }
        },
      }),
      render: (_, record) => {
        const libTypes = record?.types;
        return record?.warehouse?.name || record?.warehouse?.libraryNumber ? (
          <>
            <div className="table-data-with-id">
              {record?.warehouse?.name || '-'}
            </div>
            {record?.warehouse?.libraryNumber && (
              <div className="font-size-12">
                ({record?.warehouse?.libraryNumber || '-'})
              </div>
            )}
          </>
        ) : (
          <>
            {hasUpdatePermission &&
            !libTypes?.includes(LIBRARY_FACILITY_TYPE?.WAREHOUSE?.value) ? (
              <Button
                type="link"
                className="common-btn p-0"
                onClick={(e) => {
                  e?.stopPropagation();
                  setLibrary(record);
                  setIsUpdateWarehouse(false);
                  setIsWarehouseModalOpen(true);
                }}
              >
                Assign WareHouse
              </Button>
            ) : (
              '-'
            )}
          </>
        );
      },
    },
    {
      title: 'BACKUP LIBRARY',
      dataIndex: 'backupLibrary',
      key: 'backupLibrary',
      ellipsis: true,
      width: 200,
      align: 'left',
      className: 'max-width-column',
      onCell: (record) => ({
        onClick: (e) => {
          e?.stopPropagation();
          if (record?.backUpLibrary?.id) {
            history?.push(
              `${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}${ROUTES?.BASIC_DETAILS}/view/${record?.backUpLibrary?.id}/${record?.backUpLibrary?.libraryNumber}`,
            );
          }
        },
      }),
      render: (_, record) =>
        record?.backUpLibrary?.name || record?.backUpLibrary?.libraryNumber ? (
          <>
            <div className="table-data-with-id">
              {record?.backUpLibrary?.name || '-'}
            </div>
            <div className="font-size-12">
              ({record?.backUpLibrary?.libraryNumber || '-'})
            </div>
          </>
        ) : (
          <>
            {hasUpdatePermission ? (
              <Button
                type="link"
                className="common-btn p-0"
                onClick={(e) => {
                  e?.stopPropagation();
                  setLibrary(record);
                  setIsUpdateBackup(false);
                  setBackupLibModalOpen(true);
                }}
              >
                Assign Backup Library
              </Button>
            ) : (
              '-'
            )}
          </>
        ),
    },
    // commenting this for now as per Client's request
    // {
    //   title: 'DELIVERY PARTNER',
    //   dataIndex: 'assignDeliveryPartner',
    //   key: 'assignDeliveryPartner',
    //   ellipsis: true,
    //   width: 200,
    //   align: 'left',
    //   className: 'max-width-column',
    //   onCell: (record) => ({
    //     onClick: (e) => {
    //       e?.stopPropagation();
    //       const id = record?.deliveryPartner?.id;
    //       if (id) {
    //         history?.push(
    //           `${ROUTES?.SYSTEM_USERS}${ROUTES?.DELIVERY_PARTNERS}/view/${id}`,
    //         );
    //       }
    //     },
    //   }),
    //   render: (_, record) => {
    //     const deliveryPartner = record?.deliveryPartner;
    //     const { firstName, lastName, userNumber } = deliveryPartner || {};
    //     return firstName || lastName || userNumber ? (
    //       <>
    //         {firstName || lastName ? (
    //           <div className="table-data-with-id">{`${firstName || ''} ${
    //             lastName || ''
    //           }`}</div>
    //         ) : (
    //           '-'
    //         )}
    //         <div className="font-size-12">({userNumber || '-'})</div>
    //       </>
    //     ) : (
    //       <>
    //         {hasUpdatePermission ? (
    //           <Button
    //             type="link"
    //             className="common-btn p-0"
    //             onClick={(e) => {
    //               e?.stopPropagation();
    //               setLibrary(record);
    //               setIsUpdateDeliveryPartner(false);
    //               setDeliveryPartnerModalOpen(true);
    //             }}
    //           >
    //             Assign Delivery Partner
    //           </Button>
    //         ) : (
    //           '-'
    //         )}
    //       </>
    //     );
    //   },
    // },
    {
      title: 'LAT',
      dataIndex: 'lat',
      key: 'lat',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.latLong?.coordinates?.[1] || '-',
    },
    {
      title: 'LNG',
      dataIndex: 'lng',
      key: 'lng',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.latLong?.coordinates?.[0] || '-',
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <Badge
          color={
            record?.isActive ? STATUS?.ACTIVE?.color : STATUS?.INACTIVE?.color
          }
          text={
            record?.isActive ? STATUS?.ACTIVE?.label : STATUS?.INACTIVE?.label
          }
        />
      ),
      ...filterPopup('status'),
    },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      ellipsis: true,
      width: 150,
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'right' : false,
      className: 'cursor-auto',
      onCell: () => ({
        onClick: (e) => e?.stopPropagation(),
      }),
      render: (_, record) => {
        const deliveryPartner = record?.deliveryPartner;
        const { id, userNumber } = deliveryPartner || {};
        return (
          <>
            <div className="action-button">
              {isArchiveFilter ? (
                <Tooltip title="Unarchive" placement="top" zIndex={4}>
                  <UnarchiveIcon
                    onClick={(e) => {
                      e?.stopPropagation();
                      setIsArchivePrompts(true);
                      setLibrary(record);
                    }}
                    className="mr-16 pointer svg-icon"
                  />
                </Tooltip>
              ) : (
                <>
                  <div className="mr-16 d-flex">
                    <Tooltip
                      title="Update Backup Library"
                      placement="top"
                      className="table-action-button"
                      zIndex={4}
                    >
                      <Button
                        disabled={
                          !record?.backUpLibrary?.name &&
                          !record?.backUpLibrary?.libraryNumber
                        }
                        type="link"
                        onClick={(e) => {
                          e?.stopPropagation();
                          setLibrary(record);
                          setIsUpdateBackup(true);
                          setBackupLibModalOpen(true);
                        }}
                      >
                        <UpdateIcon
                          className={
                            !record?.backUpLibrary?.name &&
                            !record?.backUpLibrary?.libraryNumber
                              ? 'svg-icon-grey'
                              : 'svg-icon'
                          }
                        />
                      </Button>
                    </Tooltip>
                  </div>
                  <div className="mr-16 d-flex">
                    <Tooltip
                      title="Update Warehouse"
                      placement="top"
                      className="table-action-button"
                      zIndex={4}
                    >
                      <Button
                        disabled={
                          (!record?.warehouse?.name &&
                            !record?.warehouse?.libraryNumber) ||
                          record?.types?.includes(
                            LIBRARY_FACILITY_TYPE?.WAREHOUSE?.value,
                          )
                        }
                        type="link"
                        onClick={(e) => {
                          e?.stopPropagation();
                          setLibrary(record);
                          setIsUpdateWarehouse(true);
                          setIsWarehouseModalOpen(true);
                        }}
                      >
                        <WarehouseIcon
                          className={
                            (!record?.warehouse?.name &&
                              !record?.warehouse?.libraryNumber) ||
                            record?.types?.includes(
                              LIBRARY_FACILITY_TYPE?.WAREHOUSE?.value,
                            )
                              ? 'svg-icon-grey'
                              : 'svg-icon'
                          }
                        />
                      </Button>
                    </Tooltip>
                  </div>
                  {/*
                  commenting this for now as per Client's request
                  <div className="mr-16 d-flex">
                    <Tooltip
                      title="Update Delivery Partner"
                      placement="top"
                      className="table-action-button"
                      zIndex={4}
                    >
                      <Button
                        disabled={!id && !userNumber}
                        type="link"
                        onClick={(e) => {
                          e?.stopPropagation();
                          setLibrary(record);
                          setIsUpdateDeliveryPartner(true);
                          setDeliveryPartnerModalOpen(true);
                        }}
                      >
                        <DeliveryIcon
                          className={
                            !id && !userNumber ? 'svg-icon-grey' : 'svg-icon'
                          }
                        />
                      </Button>
                    </Tooltip>
                  </div> */}
                  {roles?.includes(ROLE_KEYS.SUPER_ADMIN) && (
                    <Tooltip
                      title={record?.isActive ? 'Active' : 'Inactive'}
                      placement="top"
                      zIndex={4}
                    >
                      <ActiveIcon
                        onClick={(e) => {
                          e?.stopPropagation();
                          setIsActivePrompts(true);
                          setLibrary(record);
                        }}
                        className={
                          record?.isActive
                            ? 'mr-16 pointer svg-icon'
                            : 'mr-16 pointer svg-icon-grey'
                        }
                      />
                    </Tooltip>
                  )}
                  <Tooltip title="Edit" placement="top" zIndex={4}>
                    <EditIcon
                      onClick={(e) => {
                        e?.stopPropagation();
                        history?.push(
                          `${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}${ROUTES?.BASIC_DETAILS}/edit/${record?.id}/${record?.libraryNumber}`,
                        );
                      }}
                      className="mr-16 pointer svg-icon"
                    />
                  </Tooltip>
                  {roles?.includes(ROLE_KEYS.SUPER_ADMIN) && (
                    <Tooltip title="Archive" placement="top" zIndex={4}>
                      <ArchiveIcon
                        onClick={(e) => {
                          e?.stopPropagation();
                          setIsArchivePrompts(true);
                          setLibrary(record);
                        }}
                        className="mr-16 pointer svg-icon"
                      />
                    </Tooltip>
                  )}
                  <Tooltip title="Reset Password" placement="top" zIndex={4}>
                    <ResetpasswordIcon
                      onClick={(e) => {
                        e?.stopPropagation();
                        setIsResetPrompts(true);
                        setLibrary(record);
                      }}
                      className="mr-16 pointer svg-icon"
                    />
                  </Tooltip>
                </>
              )}
            </div>
          </>
        );
      },
    },
  ]?.filter((item) => {
    if (
      (isArchiveFilter && item?.dataIndex === 'backupLibrary') ||
      (item?.dataIndex === 'actions' && !hasUpdatePermission) ||
      (item?.dataIndex === 'actions' &&
        !roles?.includes(ROLE_KEYS.SUPER_ADMIN) &&
        isArchiveFilter)
    ) {
      return item?.hidden;
    }
    return !item?.hidden;
  });

  const locale = {
    emptyText: isEmptyLibraryList ? '' : <span />,
  };

  return (
    <>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isActivePrompts}
        onOk={() => handleActive(library?.id, library?.isActive)}
        onCancel={() => setIsActivePrompts(false)}
        okButtonProps={{ loading: isActiveLoading }}
      >
        Are you sure you want to
        <strong>
          {' '}
          {!library?.isActive
            ? STATUS?.ACTIVE?.label
            : STATUS?.INACTIVE?.label}{' '}
        </strong>
        this library?
      </Modal>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isArchivePrompts}
        onOk={() => handleArchive(library?.id, library?.isArchived)}
        onCancel={() => setIsArchivePrompts(false)}
        okButtonProps={{ loading: isArchiveLoading }}
      >
        Are you sure you want to
        <strong>
          {' '}
          {!library?.isArchived
            ? ARCHIVING_STATE?.ARCHIVED
            : ARCHIVING_STATE?.UNARCHIVED}{' '}
        </strong>
        this library?
      </Modal>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isResetPrompts}
        onOk={() => handleResetPassword(library?.librarian?.email)}
        onCancel={() => setIsResetPrompts(false)}
        okButtonProps={{ loading: isResetPassLoading }}
      >
        By confirming, link will be sent to {library?.librarian?.email} to{' '}
        <strong>reset the password.</strong>
      </Modal>
      <BackupLibModal
        form={form}
        library={library}
        setLibrary={setLibrary}
        onFinish={handleAssignBackupLib}
        loadings={isAssignLibLoading}
        isModalOpen={isBackupLibModalOpen}
        setIsModalOpen={setBackupLibModalOpen}
        title={
          isUpdateBackup ? 'Update Backup Library' : 'Assign Backup Library'
        }
      />
      <WarehouseModal
        form={warehouseForm}
        library={library}
        setLibrary={setLibrary}
        onFinish={handleAssignWarehouse}
        loadings={isAssignWarehouseLoading}
        isModalOpen={isWarehouseModalOpen}
        setIsModalOpen={setIsWarehouseModalOpen}
        title={isUpdateWarehouse ? 'Update Warehouse' : 'Assign Warehouse'}
      />
      <AssignDeliveryPartnerModal
        form={form}
        library={library}
        setLibrary={setLibrary}
        onFinish={handleAssignDeliveryPartner}
        loadings={isAssignDeliveryPartnerLoading}
        isModalOpen={isDeliveryPartnerModalOpen}
        setIsModalOpen={setDeliveryPartnerModalOpen}
        title={
          isUpdateDeliveryPartner
            ? 'Update Assigned Delivery Partner'
            : 'Assign Delivery Partner'
        }
      />
      <div className="d-flex justify-end mb-16 flex-wrap">
        {hasUpdatePermission && (
          <div className="mr-16 d-flex align-center">
            <h4 className="m-0 mr-8">Only Archived</h4>{' '}
            <Switch
              size="small"
              onChange={(checked) => setIsArchiveFilter(checked)}
            />
          </div>
        )}
        <SearchComponent
          className="search-box"
          query={query}
          setQuery={setQuery}
          getData={handleSearch}
        />
      </div>
      <CommonTable
        locale={locale}
        columns={columns}
        data={libraryList || []}
        loading={isLibraryLoading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(record) => record?.id}
        rowClassName="pointer table-cell"
        onRow={(record) => ({
          onClick: () => {
            history?.push(
              `${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}${ROUTES?.BASIC_DETAILS}/view/${record?.id}/${record?.libraryNumber}`,
            );
          },
        })}
      />
    </>
  );
};

export default LibraryTable;
